import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { getArticleBySlug, articlesData } from '@/mocks/articles';

export default function Article() {
  const { lang = 'en', slug } = useParams<{ lang: string; slug: string }>();
  const { t, i18n } = useTranslation();
  const [activeSection, setActiveSection] = useState('');

  const article = getArticleBySlug(slug || '');

  // Fallback to first article if slug not found
  const safeArticle = article || articlesData[0];

  useEffect(() => {
    if (lang && ['en', 'fr', 'da', 'de'].includes(lang)) {
      i18n.changeLanguage(lang);
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [lang, i18n, slug]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -80% 0px' }
    );

    const headings = document.querySelectorAll('section[id]');
    headings.forEach((h) => observer.observe(h));

    return () => observer.disconnect();
  }, [slug]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="w-full px-4 sm:px-6 lg:px-10 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm text-foreground-600 mb-8">
            <a href={`/${lang}/`} className="hover:text-primary-600 transition-colors">{t('breadcrumb_home')}</a>
            <i className="ri-arrow-right-s-line text-foreground-400" />
            <span className="text-foreground-700">{safeArticle.category}</span>
            <i className="ri-arrow-right-s-line text-foreground-400" />
            <span className="text-foreground-700">{safeArticle.title.split(':')[0]}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
            {/* Sidebar - TOC */}
            <aside className="hidden lg:block lg:col-span-1">
              <div className="sticky top-24">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4 uppercase tracking-wider">{t('article_toc')}</h3>
                <nav className="space-y-1">
                  {safeArticle.toc.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`block w-full text-left text-sm px-3 py-2 rounded-lg transition-colors ${
                        activeSection === item.id
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      {item.title}
                    </button>
                  ))}
                </nav>

                {/* Related Articles */}
                <div className="mt-8 pt-6 border-t border-background-200">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-4 uppercase tracking-wider">{t('article_related')}</h3>
                  <div className="space-y-2">
                    {safeArticle.relatedArticles.map((article) => (
                      <a
                        key={article.slug}
                        href={`/${lang}/article/${article.slug}`}
                        className="block text-sm text-foreground-600 hover:text-primary-600 transition-colors py-1"
                      >
                        {article.title}
                      </a>
                    ))}
                  </div>
                </div>

                {/* Back to Home */}
                <div className="mt-8 pt-6 border-t border-background-200">
                  <a
                    href={`/${lang}/`}
                    className="inline-flex items-center gap-2 text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors"
                  >
                    <i className="ri-arrow-left-line" />
                    Back to Home
                  </a>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <article className="lg:col-span-3">
              {/* Article Header Image */}
              <div className="relative rounded-xl overflow-hidden mb-10 shadow-lg">
                <img
                  src={safeArticle.heroImg}
                  alt={safeArticle.title}
                  className="w-full h-64 md:h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="inline-block px-3 py-1 bg-primary-600 text-white text-xs font-medium rounded-full mb-3">
                    {safeArticle.heroTag}
                  </span>
                </div>
              </div>

              {/* Article Header */}
              <header className="mb-10">
                <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                  {safeArticle.h1}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line" />
                    {t('article_author')}: Research Team
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line" />
                    {t('article_published')}: August 2026
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-refresh-line" />
                    {t('article_updated')}: {today}
                  </span>
                </div>
              </header>

              {/* Mobile TOC - visible only on mobile */}
              <div className="lg:hidden mb-8 p-4 bg-background-100 rounded-xl border border-background-200">
                <h3 className="text-sm font-semibold text-foreground-900 mb-3 uppercase tracking-wider">{t('article_toc')}</h3>
                <nav className="space-y-1 max-h-64 overflow-y-auto">
                  {safeArticle.toc.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className="block w-full text-left text-sm px-3 py-2 rounded-lg text-foreground-600 hover:bg-white hover:text-foreground-900 transition-colors"
                    >
                      {item.title}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Article Body */}
              <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed space-y-6">
                {safeArticle.sections.map((section) => (
                  <section key={section.id} id={section.id} className="scroll-mt-24">
                    <h2 className="text-2xl font-bold text-foreground-950 mt-10 mb-4">{section.title}</h2>
                    {section.paragraphs.map((para, pIdx) => (
                      <p key={pIdx} className={pIdx === 0 ? 'text-base' : ''}>
                        {para.includes('AllChinaBuy Spreadsheet') || para.includes('AllChinaBuy') ? (
                          <>
                            {para.split(/(AllChinaBuy(?: Spreadsheet)?)/g).map((part, partIdx) => {
                              if (part === 'AllChinaBuy' || part === 'AllChinaBuy Spreadsheet') {
                                return (
                                  <a
                                    key={partIdx}
                                    href={`/${lang}/`}
                                    className="text-primary-600 hover:text-primary-700 font-medium"
                                  >
                                    {part}
                                  </a>
                                );
                              }
                              return part;
                            })}
                          </>
                        ) : (
                          para
                        )}
                      </p>
                    ))}
                    {section.img && (
                      <div className="my-8 rounded-xl overflow-hidden shadow-md">
                        <img
                          src={section.img}
                          alt={section.title}
                          className="w-full h-56 object-cover"
                        />
                      </div>
                    )}
                  </section>
                ))}

                {/* Conclusion */}
                <section id="conclusion" className="scroll-mt-24">
                  <h2 className="text-2xl font-bold text-foreground-950 mt-10 mb-4">{safeArticle.conclusion.title}</h2>
                  {safeArticle.conclusion.paragraphs.map((para, idx) => (
                    <p key={idx}>
                      {para.includes('AllChinaBuy Spreadsheet') || para.includes('AllChinaBuy') ? (
                        <>
                          {para.split(/(AllChinaBuy(?: Spreadsheet)?)/g).map((part, partIdx) => {
                            if (part === 'AllChinaBuy' || part === 'AllChinaBuy Spreadsheet') {
                              return (
                                <a
                                  key={partIdx}
                                  href={`/${lang}/`}
                                  className="text-primary-600 hover:text-primary-700 font-medium"
                                >
                                  {part}
                                </a>
                              );
                            }
                            return part;
                          })}
                        </>
                      ) : (
                        para
                      )}
                    </p>
                  ))}
                </section>
              </div>

              {/* Sources */}
              <div className="mt-12 pt-8 border-t border-background-200">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4 uppercase tracking-wider">{t('article_sources')}</h3>
                <ul className="space-y-2">
                  {safeArticle.sources.map((source, index) => (
                    <li key={index} className="text-sm text-foreground-600 flex items-start gap-2">
                      <span className="text-primary-600 mt-0.5">{index + 1}.</span>
                      {source}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Related Articles Grid */}
              <div className="mt-12 pt-8 border-t border-background-200">
                <h3 className="text-sm font-semibold text-foreground-900 mb-4 uppercase tracking-wider">Related Articles</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {safeArticle.relatedArticles.map((ra) => (
                    <a
                      key={ra.slug}
                      href={`/${lang}/article/${ra.slug}`}
                      className="group p-4 bg-background-100 rounded-xl border border-background-200 hover:border-primary-200 hover:bg-primary-50/50 transition-all"
                    >
                      <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-700 transition-colors mb-1">
                        {ra.title}
                      </h4>
                      <span className="text-xs text-primary-600 flex items-center gap-1">
                        Read Article <i className="ri-arrow-right-line" />
                      </span>
                    </a>
                  ))}
                </div>
              </div>

              {/* Internal Link Back to Home */}
              <div className="mt-10 p-6 bg-primary-50 rounded-xl border border-primary-100">
                <p className="text-sm text-foreground-700 mb-3">
                  Ready to explore more resources? Return to our main hub for additional guides, reviews, and research tools.
                </p>
                <a
                  href={`/${lang}/`}
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors"
                >
                  <i className="ri-home-line" />
                  Back to AllChinaBuy Spreadsheet Home
                </a>
              </div>
            </article>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}